<template>
  <div>
    <!-- Delete Modal -->
    <DeleteModal modal-id="modal-delete" @deleteToggle="deleteItem" />

    <!-- Description & Result Modal -->
    <b-modal
      id="modal-description-result"
      :title="$t('incubator.description-result')"
      size="lg"
      hide-footer
      centered
    >
      <b-card-text>
        <!-- Description -->
        <b-form-group
          :label="$t('common.description')"
          label-for="view_description"
        >
          <b-form-textarea
            id="view_description"
            v-model="viewData.description"
            :placeholder="$t('common.no-description')"
            rows="4"
            readonly
          />
        </b-form-group>

        <!-- Result -->
        <b-form-group
          :label="$t('incubator.result')"
          label-for="view_result"
          class="mt-3"
        >
          <b-form-textarea
            id="view_result"
            v-model="viewData.result"
            :placeholder="$t('incubator.no-result')"
            rows="4"
            readonly
          />
        </b-form-group>

        <!-- Close Button -->
        <div class="d-flex justify-content-end mt-3">
          <b-button
            variant="primary"
            @click="$bvModal.hide('modal-description-result')"
          >
            {{ $t("common.close") }}
          </b-button>
        </div>
      </b-card-text>
    </b-modal>

    <!-- Invoices Details Modal -->
    <b-modal
      id="modal-invoices"
      :title="$t('incubator.invoices-details')"
      size="xl"
      hide-footer
      centered
    >
      <b-table
        :items="selectedInvoices"
        :fields="invoiceTableColumns"
        responsive
        striped
        class="mb-0"
      >
        <!-- Invoice Number -->
        <template #cell(invoice_no)="data">
          <span class="font-weight-bold">#{{ data.item.invoice_no }}</span>
        </template>

        <!-- Amount -->
        <template #cell(amount)="data">
          <span class="font-weight-bold text-success">
            {{ formatPrice(data.item.amount) }}
          </span>
        </template>

        <!-- Discount -->
        <template #cell(discount)="data">
          <span class="text-warning">
            {{ formatPrice(data.item.discount) }}
          </span>
        </template>

        <!-- Status -->
        <template #cell(status)="data">
          <b-badge
            :variant="getInvoiceStatusVariant(data.item.status)"
            style="font-size: 0.9rem"
          >
            {{ $t(`accounting.status-${data.item.status}`) }}
          </b-badge>
        </template>

        <!-- Date -->
        <template #cell(date)="data">
          <span class="text-nowrap">{{ data.item.date }}</span>
        </template>

        <!-- Created At -->
        <template #cell(created_at)="data">
          <span class="text-nowrap text-muted">{{ data.item.created_at }}</span>
        </template>
      </b-table>
    </b-modal>

    <!-- Create New Invoice Modal -->
    <b-modal
      id="modal-create-invoice"
      :title="$t('incubator.create-new-invoice')"
      size="md"
      hide-footer
      centered
      :no-close-on-backdrop="true"
    >
      <b-card-text>
        <!-- Patient Info (Read-only Display) -->
        <b-alert variant="info" show class="mb-3">
          <div class="d-flex flex-column">
            <strong>{{ $t("reception.patient-name") }}:</strong>
            <span>{{ newInvoicePatientName }}</span>
            <strong class="mt-2">{{ $t("incubator.incubator") }}:</strong>
            <span>{{ newInvoiceIncubatorName }}</span>
          </div>
        </b-alert>

        <!-- Amount Input -->
        <b-form-group
          :label="$t('incubator.amount')"
          label-for="invoice_amount"
        >
          <b-form-input
            id="invoice_amount"
            v-model="newInvoiceForm.amount"
            type="number"
            min="0"
            :placeholder="$t('incubator.amount')"
          />
          <p class="text-danger mt-1">
            {{ newInvoiceError.amount }}
          </p>
        </b-form-group>

        <!-- Description (Optional) -->
        <b-form-group
          :label="$t('common.description')"
          label-for="invoice_description"
        >
          <b-form-textarea
            id="invoice_description"
            v-model="newInvoiceForm.description"
            :placeholder="$t('common.description')"
            rows="3"
          />
        </b-form-group>

        <!-- Action Buttons -->
        <div class="d-flex justify-content-end">
          <b-button
            variant="outline-secondary"
            class="mr-1"
            @click="$bvModal.hide('modal-create-invoice')"
            :disabled="loadingInvoice"
          >
            {{ $t("common.cancel") }}
          </b-button>
          <b-button
            variant="success"
            @click="createNewInvoice"
            :disabled="loadingInvoice"
          >
            <span v-if="!loadingInvoice">
              {{ $t("incubator.create-invoice") }}
            </span>
            <b-spinner v-else small />
          </b-button>
        </div>
      </b-card-text>
    </b-modal>

    <!-- Create / Edit Modal -->
    <b-modal
      id="modal-edit"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="lg"
      :title="
        createMode
          ? $t('incubator.add-patient-incubator')
          : $t('incubator.edit-patient-incubator')
      "
    >
      <b-card-text>
        <b-row>
          <!-- Patient Card -->
          <b-col md="12">
            <AutoComplete
              v-model="editForm.patient_card_id"
              :label="$t('common.Patient Card')"
              :placeholder="$t('common.Patient Card')"
              param-query="patient.name"
              :serializer="(val) => val.id"
              endpoint="patientCard"
              text-title="patient.name"
              :isRelational="true"
              :otherQuery="`include=patient&filter[type]=NICU&filter[is_active]=true`"
              keyword="patient"
            />
            <p class="text-danger mt-1">
              {{ errorForm.patient_card_id }}
            </p>
          </b-col>

          <!-- Incubator -->
          <b-col md="12">
            <AutoComplete
              v-model="editForm.incubator_id"
              :label="$t('incubator.incubator')"
              :placeholder="$t('incubator.incubator')"
              param-query="name"
              :serializer="(val) => val.id"
              endpoint="incubators"
              text-title="name"
              include="incubatorType"
              other-query="filter[is_available]=1"
              keyword="name"
            />
            <p class="text-danger mt-1">
              {{ errorForm.incubator_id }}
            </p>
          </b-col>

          <!-- Doctor -->
          <b-col md="12">
            <b-form-group :label="$t('common.doctors')">
              <v-select
                v-model="editForm.doctor_id"
                :options="doctors"
                label="name"
                :reduce="(doctor) => doctor.id"
                :placeholder="$t('common.doctors')"
              />
            </b-form-group>
            <p class="text-danger mt-1">
              {{ errorForm.doctor_id }}
            </p>
          </b-col>

          <!-- Duration Presets -->
          <b-col md="12">
            <b-form-group :label="$t('incubator.duration-preset')">
              <b-form-radio-group
                v-model="presetDuration"
                :options="durationOptions"
                buttons
                button-variant="outline-primary"
                size="sm"
                @change="applyPreset"
              />
            </b-form-group>
          </b-col>

          <!-- Number of Days (only shown for custom) -->
          <b-col md="12" v-if="presetDuration == 'custom'">
            <b-form-group
              :label="$t('incubator.number-of-days')"
              label-for="edit_days"
            >
              <b-form-input
                id="edit_days"
                v-model="editForm.numberOfDays"
                type="number"
                min="1"
                step="1"
                :placeholder="$t('incubator.enter-number-of-days')"
                @input="calculateDatesFromDays"
              />
              <small class="text-muted">{{
                $t("incubator.days-calculation-hint")
              }}</small>
              <p class="text-danger mt-1">
                {{ errorForm.numberOfDays }}
              </p>
            </b-form-group>
          </b-col>

          <!-- Amount -->
          <b-col cols="12">
            <b-form-group :label="$t('incubator.amount')" label-for="amount">
              <b-form-input
                id="amount"
                v-model="editForm.amount"
                type="number"
                min="0"
                step="0.01"
                :placeholder="$t('incubator.amount')"
              />
              <p class="text-danger mt-1">
                {{ errorForm.amount }}
              </p>
            </b-form-group>
          </b-col>

          <!-- Description -->
          <b-col md="12">
            <b-form-group
              :label="$t('common.description')"
              label-for="description"
            >
              <b-form-textarea
                id="description"
                v-model="editForm.description"
                :placeholder="$t('common.description')"
                rows="3"
              />
            </b-form-group>
          </b-col>

          <!-- Result -->
          <b-col md="12">
            <b-form-group :label="$t('incubator.result')" label-for="result">
              <b-form-textarea
                id="result"
                v-model="editForm.result"
                :placeholder="$t('incubator.result')"
                rows="3"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-text>

      <template #modal-footer="{}">
        <b-button
          variant="primary"
          @click="submitItem"
          class="m-0"
          :disabled="loading"
          block
        >
          <span v-if="!loading">{{
            createMode ? $t("common.add") : $t("common.save")
          }}</span>
          <b-spinner v-else small />
        </b-button>
      </template>
    </b-modal>

    <!-- Checkout Confirm Modal -->
    <b-modal
      id="modal-checkout"
      :title="$t('incubator.checkout_confirm_title')"
      size="md"
      centered
      hide-footer
      :no-close-on-backdrop="true"
    >
      <b-card-text class="mb-3">
        {{ $t("incubator.checkout_confirm_text") }}
      </b-card-text>

      <div class="d-flex justify-content-end">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          :disabled="loadingCheckout"
          @click="$bvModal.hide('modal-checkout')"
        >
          {{ $t("common.cancel") }}
        </b-button>
        <b-button
          variant="warning"
          :disabled="loadingCheckout"
          @click="confirmCheckout"
        >
          <span v-if="!loadingCheckout">{{ $t("incubator.checkout") }}</span>
          <b-spinner v-else small />
        </b-button>
      </div>
    </b-modal>

    <!-- Change Incubator Modal -->
    <b-modal
      id="modal-change-incubator"
      :title="$t('incubator.change-incubator')"
      size="md"
      centered
      hide-footer
      :no-close-on-backdrop="true"
    >
      <b-card-text>
        <!-- Patient Info Display -->
        <div class="patient-info-box mb-3">
          <div class="d-flex flex-column">
            <div class="mb-2">
              <strong
                class="d-block text-muted"
                style="font-size: 0.85rem; text-transform: uppercase"
                >{{ $t("reception.patient-name") }}</strong
              >
              <span class="d-block" style="font-size: 1rem; color: #2c3e50">{{
                changeIncubatorPatientName
              }}</span>
            </div>
            <div>
              <strong
                class="d-block text-muted"
                style="font-size: 0.85rem; text-transform: uppercase"
                >{{ $t("incubator.current-incubator") }}</strong
              >
              <span class="d-block" style="font-size: 1rem; color: #2c3e50">{{
                changeIncubatorCurrentName
              }}</span>
            </div>
          </div>
        </div>

        <!-- Available Incubators Dropdown -->
        <b-form-group
          :label="$t('incubator.select-new-incubator')"
          label-for="new_incubator"
        >
          <AutoComplete
            v-model="changeIncubatorForm.incubator_id"
            :placeholder="$t('incubator.select-new-incubator')"
            param-query="name"
            :serializer="(val) => val.id"
            endpoint="incubators"
            text-title="name"
            include="incubatorType"
            other-query="filter[is_available]=1"
            keyword="name"
          />
          <p class="text-danger mt-1">
            {{ changeIncubatorError.incubator_id }}
          </p>
        </b-form-group>

        <!-- Action Buttons -->
        <div class="d-flex justify-content-end mt-3">
          <b-button
            variant="outline-secondary"
            class="mr-1"
            :disabled="loadingChangeIncubator"
            @click="$bvModal.hide('modal-change-incubator')"
          >
            {{ $t("common.cancel") }}
          </b-button>
          <b-button
            variant="primary"
            :disabled="loadingChangeIncubator"
            @click="confirmChangeIncubator"
          >
            <span v-if="!loadingChangeIncubator">{{
              $t("common.assign")
            }}</span>
            <b-spinner v-else small />
          </b-button>
        </div>
      </b-card-text>
    </b-modal>

    <!-- Generate Invoice Modal -->
    <b-modal
      id="modal-generate-invoice"
      :title="$t('incubator.generate-invoice')"
      size="md"
      centered
      hide-footer
      :no-close-on-backdrop="true"
    >
      <b-card-text>
        <!-- Patient Info Display -->
        <div class="patient-info-box mb-3">
          <div class="d-flex flex-column">
            <div class="mb-2">
              <strong
                class="d-block text-muted"
                style="font-size: 0.85rem; text-transform: uppercase"
                >{{ $t("reception.patient-name") }}</strong
              >
              <span class="d-block" style="font-size: 1rem; color: #2c3e50">{{
                generateInvoicePatientName
              }}</span>
            </div>
            <div class="mb-2">
              <strong
                class="d-block text-muted"
                style="font-size: 0.85rem; text-transform: uppercase"
                >{{ $t("incubator.incubator") }}</strong
              >
              <span class="d-block" style="font-size: 1rem; color: #2c3e50">{{
                generateInvoiceIncubatorName
              }}</span>
            </div>
            <div>
              <strong
                class="d-block text-muted"
                style="font-size: 0.85rem; text-transform: uppercase"
                >{{ $t("incubator.overdue-status") }}</strong
              >
              <span
                class="d-block text-danger font-weight-bold"
                style="font-size: 1rem"
                >{{ generateInvoiceOverdueTime }}</span
              >
            </div>
          </div>
        </div>

        <!-- Duration Presets -->
        <b-form-group :label="$t('incubator.duration-preset')">
          <b-form-radio-group
            v-model="generateInvoicePresetDuration"
            :options="generateInvoiceDurationOptions"
            buttons
            button-variant="outline-primary"
            size="sm"
            @change="applyGenerateInvoicePreset"
          />
        </b-form-group>

        <!-- Number of Days (only shown for custom) -->
        <b-form-group
          v-if="generateInvoicePresetDuration === 'custom'"
          :label="$t('incubator.number-of-days')"
          label-for="generate_invoice_days"
        >
          <b-form-input
            id="generate_invoice_days"
            v-model="generateInvoiceForm.numberOfDays"
            type="number"
            min="1"
            step="1"
            :placeholder="$t('incubator.enter-number-of-days')"
            @input="calculatePaidUntilFromDays"
          />
          <small class="text-muted">
            {{ $t("incubator.days-calculation-hint") }}
          </small>
          <p class="text-danger mt-1">
            {{ generateInvoiceError.numberOfDays }}
          </p>
        </b-form-group>

        <!-- Amount Input -->
        <b-form-group
          :label="$t('incubator.invoice-amount')"
          label-for="generate_invoice_amount"
        >
          <b-form-input
            id="generate_invoice_amount"
            v-model="generateInvoiceForm.amount"
            type="number"
            min="0"
            step="0.01"
            :placeholder="$t('incubator.invoice-amount')"
            size="lg"
          />
          <small
            v-if="
              generateInvoicePresetDuration === 'custom' &&
              generateInvoiceForm.numberOfDays
            "
            class="text-muted"
          >
            {{ $t("incubator.amount-auto-calculated") }}
          </small>
          <p class="text-danger mt-1">
            {{ generateInvoiceError.amount }}
          </p>
        </b-form-group>

        <!-- Action Buttons -->
        <div class="d-flex justify-content-end mt-3">
          <b-button
            variant="outline-secondary"
            class="mr-1"
            :disabled="loadingGenerateInvoice"
            @click="$bvModal.hide('modal-generate-invoice')"
          >
            {{ $t("common.cancel") }}
          </b-button>
          <b-button
            variant="success"
            :disabled="loadingGenerateInvoice"
            @click="confirmGenerateInvoice"
          >
            <span v-if="!loadingGenerateInvoice">{{
              $t("incubator.generate-invoice")
            }}</span>
            <b-spinner v-else small />
          </b-button>
        </div>
      </b-card-text>
    </b-modal>

    <!-- Main Card -->
    <div v-if="canList">
      <b-card :title="$t('incubator.patient-incubators')">
        <div>
          <!-- Search/Filter Section -->
          <b-row>
            <b-col cols="12" md="12">
              <h2>{{ $t("incubator.filter-patient-incubators") }}</h2>
              <br />
            </b-col>

            <!-- Patient Card Filter -->
            <b-col md="4" cols="12">
              <b-form-group
                :label="$t('incubator.patient-card')"
                label-for="filter_patient_card"
              >
                <AutoComplete
                  v-model="filters.patient_card_id"
                  :placeholder="$t('incubator.patient-card')"
                  param-query="patient.name"
                  :serializer="(val) => val.id"
                  endpoint="patientCard"
                  text-title="patient.name"
                  :isRelational="true"
                  other-query="include=patient&filter[type]=NICU&filter[is_active]=true"
                  keyword="patient"
                />
              </b-form-group>
            </b-col>

            <!-- Incubator Filter -->
            <b-col md="4" cols="12">
              <b-form-group
                :label="$t('incubator.incubator')"
                label-for="filter_incubator"
              >
                <AutoComplete
                  v-model="filters.incubator_id"
                  :placeholder="$t('incubator.incubator')"
                  param-query="name"
                  :serializer="(val) => val.id"
                  endpoint="incubators"
                  text-title="name"
                  keyword="name"
                />
              </b-form-group>
            </b-col>

            <!-- Status Filter -->
            <b-col md="4" cols="12">
              <b-form-group
                :label="$t('common.status')"
                label-for="filter_status"
              >
                <v-select
                  id="filter_status"
                  v-model="filters.is_active"
                  :options="statusOptions"
                  :reduce="(o) => o.value"
                  label="label"
                  :placeholder="$t('common.status')"
                  clearable
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Search Button -->
          <b-row>
            <b-col
              cols="12"
              md="12"
              class="d-flex align-items-center justify-content-end mb-1 mb-md-0"
            >
              <b-button variant="primary" @click="getData()">
                {{ $t("common.search") }}
                <b-icon-search class="ml-1"></b-icon-search>
              </b-button>
            </b-col>
          </b-row>

          <!-- Header + View Toggle -->
          <div
            class="d-flex justify-content-between align-items-center mt-4 mb-2"
          >
            <h2 class="mb-0">{{ $t("incubator.patient-incubators-list") }}</h2>

            <div class="d-flex align-items-center">
              <!-- View Mode Toggle -->

              <b-button
                v-if="canCreate"
                variant="primary"
                @click="show(null, 'create')"
              >
                <feather-icon icon="PlusIcon" size="16" class="mr-50" />
                {{ $t("incubator.add-patient-incubator") }}
              </b-button>
            </div>
          </div>

          <!-- List View -->
          <div class="d-flex align-items-center mb-2">
            <b-button-group size="sm" class="mr-2">
              <b-button
                :variant="viewMode === 'cards' ? 'primary' : 'outline-primary'"
                @click="viewMode = 'cards'"
              >
                <feather-icon icon="GridIcon" size="16" class="mr-50" />
                {{ $t("common.card_view") }}
              </b-button>
              <b-button
                :variant="viewMode === 'list' ? 'primary' : 'outline-primary'"
                @click="viewMode = 'list'"
              >
                <feather-icon icon="ListIcon" size="16" class="mr-50" />
                {{ $t("common.list_view") }}
              </b-button>
            </b-button-group>
          </div>
          <b-table
            v-if="viewMode === 'list' && !loading"
            :items="data"
            responsive
            :fields="tableColumns"
            primary-key="id"
            show-empty
            :empty-text="$t('common.no_results')"
            class="position-relative"
          >
            <!-- Payment Status Timer -->
            <template #cell(payment_status)="ctx">
              <div
                class="d-flex flex-column align-items-center"
                v-if="ctx.item.is_active"
              >
                <feather-icon
                  icon="ClockIcon"
                  size="30"
                  :class="getPaymentTimerClass(ctx.item)"
                />
                <small
                  :class="
                    isPaymentExpired(ctx.item)
                      ? 'text-danger font-weight-bold'
                      : 'text-warning font-weight-bold'
                  "
                  class="mt-1 text-nowrap"
                  style="font-size: 0.9rem"
                >
                  {{ getTimeRemaining(ctx.item) }}
                </small>
              </div>
              <div
                v-else
                class="d-flex flex-column align-items-center text-muted"
              >
                <feather-icon
                  icon="CheckCircleIcon"
                  size="30"
                  class="text-success"
                />
                <small
                  class="mt-1 text-nowrap text-success font-weight-bold"
                  style="font-size: 0.9rem"
                >
                  {{ $t("incubator.inactive_checkout_done") }}
                </small>
              </div>
            </template>

            <!-- Patient Name -->
            <template #cell(patient_name)="ctx">
              <div class="d-flex flex-column">
                <span class="font-weight-bold text-primary">
                  {{ formatPatientName(ctx.item) }}
                </span>
              </div>
            </template>

            <!-- Incubator -->
            <template #cell(incubator_name)="ctx">
              <div class="d-flex flex-column">
                <span class="font-weight-bold">
                  {{ getNestedProperty(ctx.item, "incubator.name", "N/A") }}
                </span>
              </div>
            </template>

            <!-- Timeline (Start, Paid Until, End) -->
            <!-- Timeline (Start → Paid Until → End) -->
            <!-- Timeline (Start → Paid Until → End) -->
            <template #cell(timeline)="ctx">
              <div
                class="timeline-line d-flex flex-column align-items-center text-nowrap"
                :class="{ 'tl-overdue': isPaymentExpired(ctx.item) }"
              >
                <!-- Start -->
                <div
                  class="d-flex align-items-center tl-part tl-start"
                  v-b-tooltip.hover
                  :title="$t('incubator.start-time')"
                >
                  <feather-icon icon="PlayCircleIcon" size="14" class="mr-25" />
                  {{ compactDate(ctx.item.start_time) }}
                </div>

                <div class="tl-arrow">→</div>

                <!-- Paid Until -->
                <div
                  class="d-flex align-items-center tl-part tl-paid"
                  v-b-tooltip.hover
                  :title="$t('incubator.paid-until')"
                >
                  <feather-icon icon="DollarSignIcon" size="14" class="mr-25" />
                  {{ compactDate(ctx.item.paid_until) }}
                </div>

                <template v-if="ctx.item.end_time">
                  <div class="tl-arrow">→</div>
                  <div
                    class="d-flex align-items-center tl-part tl-end"
                    v-b-tooltip.hover
                    :title="$t('incubator.end-time')"
                  >
                    <feather-icon
                      icon="StopCircleIcon"
                      size="14"
                      class="mr-25"
                    />
                    {{ compactDate(ctx.item.end_time) }}
                  </div>
                </template>
              </div>
            </template>

            <!-- Amount -->
            <template #cell(amount)="ctx">
              <span class="font-weight-bold text-success">
                {{ formatPrice(ctx.item.amount) }}
              </span>
            </template>

            <!-- Status -->
            <template #cell(is_active)="ctx">
              <b-badge
                :variant="ctx.item.is_active ? 'success' : 'danger'"
                style="font-size: 1rem"
              >
                {{
                  ctx.item.is_active
                    ? $t("common.active")
                    : $t("common.inactive")
                }}
              </b-badge>
            </template>

            <!-- Description & Result -->
            <template #cell(description_result)="ctx">
              <div class="d-flex justify-content-center">
                <b-button
                  variant="info"
                  class="cursor-pointer"
                  @click="showDescriptionResultModal(ctx.item)"
                  style="padding: 0.5rem 0.75rem"
                >
                  <feather-icon icon="InfoIcon" size="18" />
                </b-button>
              </div>
            </template>

            <!-- Invoices -->
            <template #cell(actions)="ctx">
              <b-dropdown
                right
                variant="outline-secondary"
                size="sm"
                class="no-nav"
              >
                <template #button-content>
                  <feather-icon icon="MoreVerticalIcon" />
                </template>

                <b-dropdown-item @click.stop="show(ctx.item)">
                  <feather-icon icon="EditIcon" size="14" class="mr-50" />
                  {{ $t("common.edit") }}
                </b-dropdown-item>

                <b-dropdown-item
                  @click.stop="
                    $router.push({
                      name: 'PatientIncubatorShow',
                      params: { id: ctx.item.id },
                    })
                  "
                >
                  <feather-icon icon="EyeIcon" size="14" class="mr-50" />
                  {{ $t("common.view") }}
                </b-dropdown-item>

                <b-dropdown-item
                  v-if="ctx.item.is_active"
                  @click.stop="openCheckoutConfirm(ctx.item)"
                >
                  <feather-icon icon="LogOutIcon" size="14" class="mr-50" />
                  {{ $t("incubator.checkout") }}
                </b-dropdown-item>

                <b-dropdown-item
                  v-if="ctx.item.is_active"
                  @click.stop="openChangeIncubatorModal(ctx.item)"
                >
                  <feather-icon icon="RepeatIcon" size="14" class="mr-50" />
                  {{ $t("incubator.change-incubator") }}
                </b-dropdown-item>

                <b-dropdown-item
                  v-if="ctx.item.is_active"
                  @click.stop="openGenerateInvoiceModal(ctx.item)"
                >
                  <feather-icon icon="FileTextIcon" size="14" class="mr-50" />
                  {{ $t("incubator.generate-invoice") }}
                </b-dropdown-item>

                <b-dropdown-divider />

                <b-dropdown-item
                  @click.stop="showDescriptionResultModal(ctx.item)"
                >
                  <feather-icon icon="InfoIcon" size="14" class="mr-50" />
                  {{ $t("incubator.description-result") }}
                </b-dropdown-item>

                <b-dropdown-divider />

                <b-dropdown-item
                  class="text-danger"
                  @click.stop="deleteModel(ctx.item)"
                >
                  <feather-icon icon="TrashIcon" size="14" class="mr-50" />
                  {{ $t("common.delete") }}
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>

          <!-- Card View -->
          <div v-else-if="viewMode === 'cards' && !loading">
            <b-row>
              <b-col
                v-for="item in data"
                :key="item.id"
                cols="12"
                md="6"
                lg="4"
                class="mb-2"
              >
                <b-card
                  no-body
                  class="pi-card h-100"
                  :class="{
                    'pi-card--active': !!item.is_active,
                    'pi-card--inactive': !item.is_active,
                    'pi-card--expired':
                      item.is_active && isPaymentExpired(item),
                  }"
                  @click="onCardClick($event, item.id)"
                >
                  <!-- Header -->
                  <div class="pi-card__header">
                    <div
                      class="d-flex align-items-start justify-content-between w-100"
                    >
                      <div class="d-flex align-items-start">
                        <feather-icon
                          icon="UserIcon"
                          size="18"
                          class="mr-75 pi-card__icon--primary"
                        />
                        <div>
                          <div class="pi-card__title">
                            {{ formatPatientName(item) }}
                          </div>
                        </div>
                      </div>

                      <div class="text-right">
                        <div class="pi-chip">
                          <feather-icon
                            icon="CpuIcon"
                            size="14"
                            class="mr-50"
                          />
                          {{ getNestedProperty(item, "incubator.name", "N/A") }}
                        </div>
                      </div>
                    </div>

                    <!-- Compact period line with AM/PM -->
                    <div class="mt-50">
                      <small class="pi-card__muted text-nowrap">
                        {{ shortPeriod(item) }}
                      </small>
                    </div>
                  </div>

                  <!-- Divider -->
                  <div class="pi-card__divider"></div>

                  <!-- Payment status / timer -->
                  <div class="pi-card__section">
                    <div class="d-flex align-items-center">
                      <feather-icon
                        icon="ClockIcon"
                        size="20"
                        :class="
                          item.is_active
                            ? getPaymentTimerClass(item)
                            : 'text-secondary'
                        "
                      />
                      <small
                        class="ml-75 text-nowrap"
                        :class="
                          item.is_active
                            ? isPaymentExpired(item)
                              ? 'text-danger font-weight-bold'
                              : 'text-success'
                            : 'text-muted'
                        "
                      >
                        <template v-if="item.is_active">
                          {{ getTimeRemaining(item) }}
                        </template>
                        <template v-else>
                          {{ $t("incubator.inactive_checkout_done") }}
                        </template>
                      </small>
                    </div>
                  </div>

                  <!-- Footer actions -->
                  <div class="pi-card__footer" @click.stop>
                    <!-- Generate Invoice button (always shown for active items) -->
                    <b-button
                      v-if="item.is_active"
                      size="sm"
                      variant="primary"
                      class="mr-1"
                      @click.stop="openGenerateInvoiceModal(item)"
                    >
                      <feather-icon
                        icon="FileTextIcon"
                        size="14"
                        class="mr-50"
                      />
                      {{ $t("incubator.generate-invoice") }}
                    </b-button>

                    <!-- Checkout button (always shown for active items) -->
                    <b-button
                      v-if="item.is_active"
                      size="sm"
                      variant="warning"
                      class="mr-1"
                      @click.stop="openCheckoutConfirm(item)"
                    >
                      <feather-icon icon="LogOutIcon" size="14" class="mr-50" />
                      {{ $t("incubator.checkout") }}
                    </b-button>

                    <!-- Empty spacer when no action buttons -->
                    <div v-if="!item.is_active"></div>

                    <!-- Dropdown menu for remaining actions -->
                    <b-dropdown
                      right
                      variant="outline-secondary"
                      size="sm"
                      class="no-nav"
                      @click.stop
                    >
                      <template #button-content>
                        <feather-icon icon="MoreVerticalIcon" />
                      </template>

                      <b-dropdown-item
                        v-if="canUpdate(item)"
                        @click.stop="show(item)"
                      >
                        <feather-icon icon="EditIcon" size="14" class="mr-50" />
                        {{ $t("common.edit") }}
                      </b-dropdown-item>

                      <b-dropdown-item
                        @click.stop="
                          $router.push({
                            name: 'PatientIncubatorShow',
                            params: { id: item.id },
                          })
                        "
                      >
                        <feather-icon icon="EyeIcon" size="14" class="mr-50" />
                        {{ $t("common.view") }}
                      </b-dropdown-item>

                      <b-dropdown-item
                        v-if="item.is_active"
                        @click.stop="openChangeIncubatorModal(item)"
                      >
                        <feather-icon
                          icon="RepeatIcon"
                          size="14"
                          class="mr-50"
                        />
                        {{ $t("incubator.change-incubator") }}
                      </b-dropdown-item>

                      <b-dropdown-divider />

                      <b-dropdown-item
                        @click.stop="showDescriptionResultModal(item)"
                      >
                        <feather-icon icon="InfoIcon" size="14" class="mr-50" />
                        {{ $t("incubator.description-result") }}
                      </b-dropdown-item>

                      <b-dropdown-divider v-if="canDelete(item)" />

                      <b-dropdown-item
                        v-if="canDelete(item)"
                        class="text-danger"
                        @click.stop="deleteModel(item)"
                      >
                        <feather-icon
                          icon="TrashIcon"
                          size="14"
                          class="mr-50"
                        />
                        {{ $t("common.delete") }}
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>
                </b-card>
              </b-col>
            </b-row>

            <!-- Empty state (cards) -->
            <div v-if="!data.length" class="text-center text-muted py-3">
              {{ $t("common.no_results") }}
            </div>
          </div>

          <!-- Loading -->
          <div v-else class="d-flex justify-content-center mb-3">
            <b-spinner label="Loading..." />
          </div>

          <!-- Pagination -->
          <div class="mx-2 mb-2">
            <b-row>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-start"
              >
                <span class="text-muted">
                  {{
                    $t("common.showing_range", {
                      from: fromRow,
                      to: toRow,
                      total: dataCount,
                    })
                  }}
                </span>
              </b-col>

              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-end"
                @click="getData"
              >
                <b-pagination
                  v-model="page"
                  :total-rows="dataCount"
                  :per-page="take"
                  first-number
                  last-number
                  class="mb-0 mt-1 mt-sm-0"
                  prev-class="prev-item"
                  next-class="next-item"
                >
                  <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18" />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-card>
    </div>

    <!-- No permission state -->
    <div v-else class="text-center py-5 text-muted">
      {{ $t("common.no_permission") }}
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BFormGroup,
  BButton,
  BButtonGroup,
  BCol,
  BRow,
  BFormInput,
  BFormTextarea,
  BTable,
  BPagination,
  BSpinner,
  BCardText,
  BBadge,
  BIconSearch,
  BAlert,
  BFormRadioGroup,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
} from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import vSelect from "vue-select";
import { getNestedProperty } from "@/utils/nestedProperty.js";

import permissionChecker from "@/mixins/permissionChecker";
import ActionsButtons from "@/components/common/ActionsButtons.vue";
import DeleteModal from "@/components/modals/DeleteModal.vue";

const AutoComplete = () => import("@/components/common/AutoComplete.vue");

export default {
  components: {
    BCard,
    BFormGroup,
    BButton,
    BButtonGroup,
    BCol,
    BRow,
    BFormInput,
    BFormTextarea,
    BTable,
    BPagination,
    BSpinner,
    BCardText,
    BBadge,
    BIconSearch,
    BAlert,
    BFormRadioGroup,
    vSelect,
    AutoComplete,
    ActionsButtons,
    DeleteModal,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
  },
  mixins: [permissionChecker],

  data() {
    return {
      // view mode (persisted)
      viewMode: localStorage.getItem("patientIncubatorView") || "cards",

      // paging
      take: 10,
      page: 1,
      dataCount: 0,
      loading: true,
      loadingCheckout: false,
      checkoutTarget: null,

      // change incubator
      loadingChangeIncubator: false,
      changeIncubatorTarget: null,
      changeIncubatorForm: {
        incubator_id: "",
      },
      changeIncubatorError: {
        incubator_id: "",
      },
      changeIncubatorPatientName: "",
      changeIncubatorCurrentName: "",

      // list
      data: [],

      // timer for auto-refresh
      refreshTimer: null,
      currentTime: new Date(),

      // edit/create
      createMode: false,
      idItem: null,
      doctors: [],
      editForm: {
        patient_card_id: "",
        incubator_id: "",
        doctor_id: "",
        start_time: "",
        paid_until: "",
        end_time: "",
        amount: "",
        numberOfDays: "",
        description:
          "Patient admitted to NICU with respiratory distress. Temperature: 36.8°C, Heart Rate: 145 bpm, SpO2: 94%. Started on oxygen therapy and continuous monitoring. Family has been informed of the condition and treatment plan.",
        result:
          "Patient showing significant improvement after 48 hours. Respiratory rate stabilized at 45 breaths/min. Oxygen saturation improved to 98%. Successfully weaned off oxygen support. Feeding well and maintaining body temperature. Recommended to continue monitoring for another 24 hours before discharge.",
        _method: "POST",
      },
      errorForm: {
        patient_card_id: "",
        incubator_id: "",
        doctor_id: "",
        start_time: "",
        paid_until: "",
        end_time: "",
        amount: "",
        numberOfDays: "",
        description: "",
        result: "",
      },

      // duration presets
      presetDuration: "1day",
      durationOptions: [
        { text: `1 ${this.$t("incubator.day")}`, value: "1day" },
        { text: this.$t("incubator.custom"), value: "custom" },
      ],

      // delete
      deleteForm: {},

      // new invoice form
      newInvoiceForm: {
        patient_card_id: "",
        patient_card_incubator_id: "",
        amount: "",
        description: "",
        type: "invoice_nicu",
      },
      newInvoiceError: {
        amount: "",
      },
      loadingInvoice: false,
      newInvoicePatientName: "",
      newInvoiceIncubatorName: "",

      // generate invoice for overdue
      generateInvoiceForm: {
        amount: "",
        paid_until: "",
        numberOfDays: "",
      },
      generateInvoiceError: {
        amount: "",
        paid_until: "",
        numberOfDays: "",
      },
      loadingGenerateInvoice: false,
      generateInvoiceTarget: null,
      generateInvoicePatientName: "",
      generateInvoiceIncubatorName: "",
      generateInvoiceOverdueTime: "",
      generateInvoiceBaseDailyRate: 0,
      generateInvoicePresetDuration: "1day",
      generateInvoiceDurationOptions: [
        { text: `1 ${this.$t("incubator.day")}`, value: "1day" },
        { text: this.$t("incubator.custom"), value: "custom" },
      ],

      // filters
      filters: {
        patient_card_id: "",
        incubator_id: "",
        is_active: 1,
      },

      // table columns
      tableColumns: [
        {
          key: "payment_status",
          label: this.$t("incubator.payment-status"),
          sortable: false,
          thStyle: { width: "140px" },
        },
        {
          key: "patient_name",
          label: this.$t("reception.patient-name"),
          sortable: false,
        },
        {
          key: "incubator_name",
          label: this.$t("incubator.incubator"),
          sortable: false,
        },
        {
          key: "timeline",
          label: this.$t("incubator.timeline"),
          sortable: false,
          thStyle: { width: "200px" },
        },
        {
          key: "actions",
          label: this.$t("common.actions"),
          thStyle: { width: "120px" },
        },
      ],

      // view description & result modal
      viewData: {
        description:
          "Patient admitted to NICU with respiratory distress. Temperature: 36.8°C, Heart Rate: 145 bpm, SpO2: 94%. Started on oxygen therapy and continuous monitoring. Family has been informed of the condition and treatment plan.",
        result:
          "Patient showing significant improvement after 48 hours. Respiratory rate stabilized at 45 breaths/min. Oxygen saturation improved to 98%. Successfully weaned off oxygen support. Feeding well and maintaining body temperature. Recommended to continue monitoring for another 24 hours before discharge.",
      },

      // invoice modal
      selectedInvoices: [],
      invoiceTableColumns: [
        {
          key: "invoice_no",
          label: this.$t("accounting.invoice-no"),
          sortable: false,
        },
        { key: "amount", label: this.$t("incubator.amount"), sortable: false },
        {
          key: "discount",
          label: this.$t("accounting.discount"),
          sortable: false,
        },
        { key: "status", label: this.$t("common.status"), sortable: false },
        { key: "date", label: this.$t("common.date"), sortable: false },
        {
          key: "created_at",
          label: this.$t("common.created-at"),
          sortable: false,
        },
      ],

      // select options
      statusOptions: [
        { label: this.$t("common.active"), value: 1 },
        { label: this.$t("common.inactive"), value: 0 },
      ],
    };
  },

  computed: {
    // permission flags
    canList() {
      return this.hasPermission("get-patientCardIncubator");
    },
    canCreate() {
      return this.hasPermission("store-patientCardIncubator");
    },

    // pagination
    fromRow() {
      return this.data.length > 0 ? (this.page - 1) * this.take + 1 : 0;
    },
    toRow() {
      return Math.min(this.page * this.take, this.dataCount);
    },
  },

  watch: {
    viewMode(val) {
      localStorage.setItem("patientIncubatorView", val);
    },
  },

  mounted() {
    this.getData();

    // Update clock displays every minute (60 seconds)
    this.refreshTimer = setInterval(() => {
      this.currentTime = new Date();
    }, 60000);
  },

  beforeDestroy() {
    // Clear the timer when component is destroyed
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
  },

  methods: {
    getNestedProperty,

    /** CARD CLICK HANDLER:
     * Navigate only if the user didn't click an interactive element.
     */
    onCardClick(evt, id) {
      const interactiveSelector =
        'button, a, input, textarea, select, label, [role="button"], .btn, .dropdown, .custom-control, .no-nav';
      if (evt.defaultPrevented || evt.target.closest(interactiveSelector)) {
        return;
      }
      this.$router.push({ name: "PatientIncubatorShow", params: { id } });
    },
    compactDate(dateTime) {
      if (!dateTime) return "—";
      const d = new Date(dateTime);
      if (isNaN(d)) return dateTime;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      let h = d.getHours();
      const min = String(d.getMinutes()).padStart(2, "0");
      const ampm = h >= 12 ? "PM" : "AM";
      h = h % 12 || 12;
      return `${y}-${m}-${day} ${h}:${min} ${ampm}`;
    },

    /** unified permission helper (array | object | string) */
    hasPermission(key) {
      try {
        const store = this.$store;
        if (!store || !store.state || !store.state.app) return false;
        const perms = store.state.app.permissions;
        if (!perms) return false;

        if (Array.isArray(perms)) return perms.indexOf(key) !== -1;
        if (typeof perms === "object") return !!perms[key];
        if (typeof perms === "string")
          return (
            perms
              .split(",")
              .map((s) => s.trim())
              .indexOf(key) !== -1
          );
        return false;
      } catch (e) {
        return false;
      }
    },

    // Check if user can update item
    canUpdate(item) {
      const canUpdate =
        this.hasPermission("update-patientCardIncubator") &&
        item.is_active === true;
      return canUpdate;
    },

    // Check if user can delete item
    canDelete(item) {
      return (
        this.hasPermission("delete-patientCardIncubator") &&
        item.is_active === true
      );
    },

    // Format price with currency
    formatPrice(price) {
      return price ? `${Number(price).toLocaleString()} IQD` : "0 IQD";
    },
    formatTo12Hour(dateTime) {
      if (!dateTime) return "";
      const d = new Date(dateTime);
      if (isNaN(d)) return dateTime;
      let hours = d.getHours();
      const minutes = String(d.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      return `${hours}:${minutes} ${ampm}`;
    },

    formatShortDate(dateTime) {
      if (!dateTime) return "";
      const d = new Date(dateTime);
      if (isNaN(d)) return dateTime;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },

    shortPeriod(item) {
      const start = `${this.formatShortDate(
        item.start_time
      )} ${this.formatTo12Hour(item.start_time)}`;
      const end = item.end_time
        ? `${this.formatShortDate(item.end_time)} ${this.formatTo12Hour(
            item.end_time
          )}`
        : "—";
      return `${start} → ${end}`;
    },

    // Format patient name with child name
    formatPatientName(item) {
      const parentName = getNestedProperty(
        item,
        "patient_card.patient.name",
        "N/A"
      );
      const childName = getNestedProperty(item, "patient_card.child_name", "");

      if (!childName) return parentName;
      if (childName === "ابن" || childName === "بنت") {
        return `${childName} ${parentName}`;
      }
      return `${childName} (${parentName})`;
    },

    // Show description & result modal
    showDescriptionResultModal(item) {
      this.viewData.description = item.description || "";
      this.viewData.result = item.result || "";
      this.$bvModal.show("modal-description-result");
    },

    // Show invoices modal
    showInvoicesModal(invoices) {
      this.selectedInvoices = invoices || [];
      this.$bvModal.show("modal-invoices");
    },

    // Get invoice status badge variant
    getInvoiceStatusVariant(status) {
      const variantMap = {
        paid: "success",
        pending: "warning",
        cancelled: "danger",
        partial: "info",
      };
      return variantMap[status] || "secondary";
    },

    // Check if any invoice is pending
    hasPendingInvoice(invoices) {
      if (!invoices || !Array.isArray(invoices)) return false;
      return invoices.some((invoice) => invoice.status === "pending");
    },

    // Check if payment time has expired
    isPaymentExpired(item) {
      const paidUntil = item.paid_until;
      if (!paidUntil) return false;

      const paidUntilDate = new Date(paidUntil);
      // Use this.currentTime to trigger reactivity
      const now = this.currentTime ? new Date() : new Date();

      return now >= paidUntilDate;
    },

    // Get time remaining or overdue
    getTimeRemaining(item) {
      const paidUntil = item.paid_until;
      if (!paidUntil) return this.$t("common.not-available");

      const paidUntilDate = new Date(paidUntil);
      const now = this.currentTime ? new Date() : new Date();
      const diffMs = paidUntilDate - now;

      if (diffMs <= 0) {
        const overdueDays = Math.ceil(Math.abs(diffMs) / (1000 * 60 * 60 * 24));
        return `${overdueDays}d ${this.$t("incubator.overdue_label")}`;
      }

      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      return `${diffDays}d ${this.$t("incubator.left_label")}`;
    },

    // Get CSS class for payment timer clock icon
    getPaymentTimerClass(item) {
      const expired = this.isPaymentExpired(item);
      return expired ? "clock-icon-expired" : "clock-icon-active";
    },

    // Open create invoice modal
    openCreateInvoiceModal(item) {
      // Reset form
      this.newInvoiceForm = {
        patient_card_id: item.patient_card_id,
        patient_card_incubator_id: item.id,
        amount: "",
        description: "",
        type: "invoice_nicu",
      };
      this.newInvoiceError = { amount: "" };

      // Set display names
      this.newInvoicePatientName = this.formatPatientName(item);
      this.newInvoiceIncubatorName = getNestedProperty(
        item,
        "incubator.name",
        "N/A"
      );

      // Show modal
      this.$bvModal.show("modal-create-invoice");
    },

    // Create new invoice
    async createNewInvoice() {
      this.loadingInvoice = true;
      this.newInvoiceError.amount = "";

      // Validate amount
      if (!this.newInvoiceForm.amount || this.newInvoiceForm.amount <= 0) {
        this.newInvoiceError.amount = this.$t("validation.required");
        this.loadingInvoice = false;
        return;
      }

      try {
        const payload = {
          invoices: [
            {
              patient_card_id: this.newInvoiceForm.patient_card_id,
              patient_card_incubator_id:
                this.newInvoiceForm.patient_card_incubator_id,
              amount: parseFloat(this.newInvoiceForm.amount),
              type: this.newInvoiceForm.type,
              ...(this.newInvoiceForm.description && {
                description: this.newInvoiceForm.description,
              }),
            },
          ],
        };

        await this.$store.dispatch("create", {
          form: payload,
          path: "/invoices",
        });

        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.success"),
            variant: "success",
            text: this.$t("incubator.invoice-created-successfully"),
          },
        });

        this.getData();
        this.$bvModal.hide("modal-create-invoice");
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.error"),
            variant: "danger",
            text: this.$t("common.error-occurred"),
          },
        });
      } finally {
        this.loadingInvoice = false;
      }
    },

    // LIST
    async getData() {
      this.loading = true;
      let url = `/patientCardIncubators?include=patientCard,incubator,invoices,doctor&page=${this.page}&take=${this.take}&sort=-id`;

      // Add filters
      if (this.filters.patient_card_id) {
        const patientCardId =
          typeof this.filters.patient_card_id === "object"
            ? this.filters.patient_card_id.id
            : this.filters.patient_card_id;
        url += `&filter[patient_card_id]=${encodeURIComponent(patientCardId)}`;
      }
      if (this.filters.incubator_id) {
        const incubatorId =
          typeof this.filters.incubator_id === "object"
            ? this.filters.incubator_id.id
            : this.filters.incubator_id;
        url += `&filter[incubator_id]=${encodeURIComponent(incubatorId)}`;
      }
      if (this.filters.is_active === 1 || this.filters.is_active === 0) {
        url += `&filter[is_active]=${this.filters.is_active}`;
      }

      await this.getList(url);
      this.dataCount = this.all_data.total;
      this.data = this.all_data.data;
      this.loading = false;
    },

    // Get doctors list
    async getDoctors() {
      await this.getList("doctor/nicu?take=100").then((response) => {
        this.doctors = response.data.data.map((doctor) => ({
          id: doctor.id,
          name: doctor.name,
          type: doctor.type,
        }));
      });
    },

    // OPEN MODAL
    async show(item, mode) {
      // Load doctors when modal opens
      await this.getDoctors();

      if (mode === "create") {
        this.createMode = true;
        this.idItem = null;
        this.presetDuration = "24h"; // default choice
        this.editForm = {
          patient_card_id: "",
          incubator_id: "",
          doctor_id: "",
          start_time: "",
          paid_until: "",
          end_time: "",
          amount: "",
          numberOfDays: "",
          description: "",
          result: "",
          _method: "POST",
        };
        // apply default preset now
        this.applyPreset();
      } else {
        this.createMode = false;
        this.idItem = item.id;
        this.presetDuration = "custom"; // editing existing => custom

        // Load doctor_id if item has doctor
        let doctorId = "";
        if (item.doctor_id) {
          doctorId = item.doctor_id;
        } else if (item.doctor) {
          // If doctor is an object, get its id
          if (typeof item.doctor === "object" && item.doctor.id) {
            doctorId = item.doctor.id;
          } else if (Array.isArray(item.doctor) && item.doctor.length > 0) {
            // If doctor is an array, get the first one's id
            doctorId = item.doctor[0].id || item.doctor[0];
          }
        } else if (
          item.doctors &&
          Array.isArray(item.doctors) &&
          item.doctors.length > 0
        ) {
          // Fallback to doctors array if exists
          doctorId = item.doctors[0].id || item.doctors[0];
        }

        this.editForm = {
          patient_card_id: item.patient_card_id || "",
          incubator_id: item.incubator_id || "",
          doctor_id: doctorId,
          start_time: this.formatDateTimeForInput(item.start_time) || "",
          paid_until: this.formatDateTimeForInput(item.paid_until) || "",
          end_time: this.formatDateTimeForInput(item.end_time) || "",
          amount: item.amount || "",
          numberOfDays: "",
          description: item.description || "",
          result: item.result || "",
          _method: "PUT",
        };
      }
      this.$bvModal.show("modal-edit");
    },

    // Preset handler: sets start = now, paid_until = start + N hours
    applyPreset() {
      if (this.presetDuration === "custom") {
        // Clear fields when switching to custom
        this.editForm.numberOfDays = "";
        this.editForm.start_time = "";
        this.editForm.paid_until = "";
        this.editForm.end_time = "";
        return;
      }
      const hours = this.presetDuration === "1day" ? 24 : 24;
      const start = this.roundToMinute(new Date());
      const until = new Date(start.getTime() + hours * 60 * 60 * 1000);
      this.editForm.start_time = this.toInputDateTime(start);
      this.editForm.paid_until = this.toInputDateTime(until);
      this.editForm.end_time = "";
      this.editForm.numberOfDays = "";
    },

    // Calculate start_time and paid_until from numberOfDays
    calculateDatesFromDays() {
      const days = parseInt(this.editForm.numberOfDays);
      if (!days || days <= 0) {
        this.editForm.start_time = "";
        this.editForm.paid_until = "";
        this.editForm.end_time = "";
        return;
      }
      const start = this.roundToMinute(new Date());
      const until = new Date(start.getTime() + days * 24 * 60 * 60 * 1000);
      this.editForm.start_time = this.toInputDateTime(start);
      this.editForm.paid_until = this.toInputDateTime(until);
      // End time is same as paid_until when using number of days
      this.editForm.end_time = "";

      // Don't auto-calculate amount for add patient incubator
    },

    roundToMinute(d) {
      const dt = new Date(d);
      dt.setSeconds(0, 0);
      return dt;
    },

    toInputDateTime(date) {
      // format as "YYYY-MM-DDTHH:mm"
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const h = String(date.getHours()).padStart(2, "0");
      const min = String(date.getMinutes()).padStart(2, "0");
      return `${y}-${m}-${day}T${h}:${min}`;
    },

    // Format datetime for input (convert from "YYYY-MM-DD hh:mm:ss am/pm" or ISO to "YYYY-MM-DDTHH:mm")
    formatDateTimeForInput(dateTime) {
      if (!dateTime) return "";
      try {
        const date = new Date(dateTime);
        if (isNaN(date)) return "";
        return this.toInputDateTime(date);
      } catch (e) {
        return "";
      }
    },

    // Format datetime for API (convert from "YYYY-MM-DDTHH:mm" to "YYYY-MM-DD HH:mm:ss")
    formatDateTimeForAPI(dateTime) {
      if (!dateTime) return null;
      return dateTime.replace("T", " ") + ":00";
    },

    // DELETE
    deleteModel(item) {
      this.deleteForm = item;
      this.$bvModal.show("modal-delete");
    },

    deleteItem() {
      this.$store
        .dispatch("delete", `/patientCardIncubators/${this.deleteForm.id}`)
        .then(() => {
          this.getData();
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("common.delete_success"),
              variant: "success",
            },
          });
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("common.error"),
              variant: "danger",
              text: this.$t("common.error-occurred"),
            },
          });
        });
    },

    // SUBMIT
    submitItem() {
      this.loading = true;

      // reset errors
      Object.keys(this.errorForm).forEach((k) => (this.errorForm[k] = ""));

      // build/clean
      const payload = { ...this.editForm };

      // Extract IDs from objects if AutoComplete returned full objects
      if (
        typeof payload.patient_card_id === "object" &&
        payload.patient_card_id
      ) {
        payload.patient_card_id = payload.patient_card_id.id || "";
      }
      if (typeof payload.incubator_id === "object" && payload.incubator_id) {
        payload.incubator_id = payload.incubator_id.id || "";
      }
      if (typeof payload.doctor_id === "object" && payload.doctor_id) {
        payload.doctor_id = payload.doctor_id.id || "";
      }

      // If a preset is chosen, ensure fields are set (safety)
      if (this.presetDuration !== "custom") {
        this.applyPreset();
      } else {
        // For custom preset, recalculate from numberOfDays if needed
        if (
          this.editForm.numberOfDays &&
          parseInt(this.editForm.numberOfDays) > 0
        ) {
          this.calculateDatesFromDays();
        }
      }

      // Format datetime fields for API
      if (payload.start_time) {
        payload.start_time = this.formatDateTimeForAPI(payload.start_time);
      }
      if (payload.paid_until) {
        payload.paid_until = this.formatDateTimeForAPI(payload.paid_until);
      }
      if (payload.end_time) {
        payload.end_time = this.formatDateTimeForAPI(payload.end_time);
      }

      // Remove numberOfDays before sending to API (it's only for UI calculation)
      delete payload.numberOfDays;

      // Remove empty fields
      Object.keys(payload).forEach((k) => {
        if (
          payload[k] === "" ||
          payload[k] === null ||
          (Array.isArray(payload[k]) && payload[k].length === 0)
        ) {
          delete payload[k];
        }
      });

      // validations
      let invalid = false;
      if (!payload.patient_card_id) {
        this.errorForm.patient_card_id = this.$t("validation.required");
        invalid = true;
      }
      if (!payload.incubator_id) {
        this.errorForm.incubator_id = this.$t("validation.required");
        invalid = true;
      }
      if (!payload.start_time) {
        this.errorForm.start_time = this.$t("validation.required");
        invalid = true;
      }
      // Validate numberOfDays for custom preset
      if (this.presetDuration === "custom") {
        const days = parseInt(this.editForm.numberOfDays);
        if (!days || days <= 0) {
          this.errorForm.numberOfDays = this.$t("validation.required");
          invalid = true;
        }
      }

      if (invalid) {
        this.loading = false;
        return;
      }

      if (this.createMode) {
        if (!this.canCreate) {
          this.loading = false;
          return;
        }
        const params = { form: payload, path: "/patientCardIncubators" };
        this.$store
          .dispatch("create", params)
          .then(() => {
            this.getData();
            this.$bvModal.hide("modal-edit");
            this.loading = false;
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: this.$t("common.success"),
                variant: "success",
                text: this.$t("incubator.patient-incubator-created"),
              },
            });
          })
          .catch((error) => {
            this.loading = false;
            if (error.response && error.response.status === 400) {
              for (const item in error.response.data) {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: this.$t("validation.error"),
                    variant: "danger",
                    text: error.response.data[item][0],
                  },
                });
              }
            }
          });
      } else {
        if (!this.hasPermission("update-patientCardIncubator")) {
          this.loading = false;
          return;
        }
        const params = {
          form: payload,
          path: `/patientCardIncubators/${this.idItem}`,
        };
        this.$store
          .dispatch("update", params)
          .then(() => {
            this.getData();
            this.$bvModal.hide("modal-edit");
            this.loading = false;
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: this.$t("common.success"),
                variant: "success",
                text: this.$t("incubator.patient-incubator-updated"),
              },
            });
          })
          .catch((error) => {
            this.loading = false;
            if (error.response && error.response.status === 400) {
              for (const item in error.response.data) {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: this.$t("validation.error"),
                    variant: "danger",
                    text: error.response.data[item][0],
                  },
                });
              }
            }
          });
      }
    },

    openCheckoutConfirm(item) {
      this.checkoutTarget = item || null;
      this.$bvModal.show("modal-checkout");
    },

    async confirmCheckout() {
      if (!this.checkoutTarget) return;
      this.loadingCheckout = true;

      try {
        await this.$store.dispatch("update", {
          form: { _method: "PUT" },
          path: `/patientCardIncubators/${this.checkoutTarget.id}/checkout`,
        });

        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.success"),
            variant: "success",
            text: this.$t("incubator.checkout_success"),
          },
        });

        this.$bvModal.hide("modal-checkout");
        this.checkoutTarget = null;
        await this.getData();
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.error"),
            variant: "danger",
            text: this.$t("incubator.checkout_error"),
          },
        });
      } finally {
        this.loadingCheckout = false;
      }
    },

    openChangeIncubatorModal(item) {
      this.changeIncubatorTarget = item || null;
      this.changeIncubatorForm.incubator_id = "";
      this.changeIncubatorError.incubator_id = "";

      // Set patient and current incubator names for display
      this.changeIncubatorPatientName = this.formatPatientName(item);
      this.changeIncubatorCurrentName = getNestedProperty(
        item,
        "incubator.name",
        "N/A"
      );

      this.$bvModal.show("modal-change-incubator");
    },

    async confirmChangeIncubator() {
      if (!this.changeIncubatorTarget) return;

      // Validate incubator selection
      if (!this.changeIncubatorForm.incubator_id) {
        this.changeIncubatorError.incubator_id = this.$t("validation.required");
        return;
      }

      this.loadingChangeIncubator = true;
      this.changeIncubatorError.incubator_id = "";

      try {
        const payload = {
          patient_card_incubator_id: this.changeIncubatorTarget.id,
          new_incubator_id: this.changeIncubatorForm.incubator_id,
        };

        await this.$store.dispatch("create", {
          form: payload,
          path: `/incubators/changeIncubator`,
        });

        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.success"),
            variant: "success",
            text: this.$t("incubator.incubator-changed-successfully"),
          },
        });

        this.$bvModal.hide("modal-change-incubator");
        this.changeIncubatorTarget = null;
        await this.getData();
      } catch (e) {
        console.error("Change Incubator Error:", e);
        console.error("Error Response:", e?.response?.data);
        const errorMsg =
          e?.response?.data?.message || this.$t("common.error-occurred");
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.error"),
            variant: "danger",
            text: errorMsg,
          },
        });
      } finally {
        this.loadingChangeIncubator = false;
      }
    },

    // Open generate invoice modal
    async openGenerateInvoiceModal(item) {
      // Reset form
      this.generateInvoiceForm = {
        amount: "",
        paid_until: "",
        numberOfDays: "",
      };
      this.generateInvoiceError = {
        amount: "",
        paid_until: "",
        numberOfDays: "",
      };
      this.generateInvoiceTarget = item;
      this.generateInvoicePresetDuration = "1day";

      // Set display names
      this.generateInvoicePatientName = this.formatPatientName(item);
      this.generateInvoiceIncubatorName = getNestedProperty(
        item,
        "incubator.name",
        "N/A"
      );
      this.generateInvoiceOverdueTime = this.getTimeRemaining(item);

      // Ensure incubator data includes type/pricing information
      await this.ensureIncubatorDetailsWithType(item);
      this.setGenerateInvoiceBaseDailyRate();

      // Apply default preset (1day) and set default number of days
      this.generateInvoiceForm.numberOfDays = 1;
      this.applyGenerateInvoicePreset();
      this.calculateGenerateInvoiceAmount();

      // Show modal
      this.$bvModal.show("modal-generate-invoice");
    },

    // Apply preset for generate invoice modal
    applyGenerateInvoicePreset() {
      if (this.generateInvoicePresetDuration === "custom") {
        // Clear the paid_until and numberOfDays when switching to custom
        this.generateInvoiceForm.paid_until = "";
        this.generateInvoiceForm.numberOfDays = "";
        this.generateInvoiceForm.amount = "";
        return;
      }

      // Set paid_until to 24 hours from now for "1day" preset
      const now = this.roundToMinute(new Date());
      const until = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      this.generateInvoiceForm.paid_until = this.toInputDateTime(until);
      this.generateInvoiceForm.numberOfDays = 1;
      this.calculateGenerateInvoiceAmount();
    },

    // Calculate paid_until from number of days
    calculatePaidUntilFromDays() {
      const days = parseInt(this.generateInvoiceForm.numberOfDays);
      if (!days || days <= 0) {
        this.generateInvoiceForm.paid_until = "";
        this.generateInvoiceForm.amount = "";
        return;
      }

      // Calculate paid_until = now + (days * 24 hours)
      const now = this.roundToMinute(new Date());
      const until = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
      this.generateInvoiceForm.paid_until = this.toInputDateTime(until);

      // Calculate amount based on incubator type price
      this.calculateGenerateInvoiceAmount();
    },

    // Calculate amount for generate invoice based on incubator type price and number of days
    calculateGenerateInvoiceAmount() {
      const days = parseInt(this.generateInvoiceForm.numberOfDays, 10);
      if (!days || days <= 0) {
        this.generateInvoiceForm.amount = "";
        return;
      }

      // Get price from generateInvoiceTarget incubator's type (check multiple possible property names)
      const baseRate = this.generateInvoiceBaseDailyRate;

      if (baseRate && baseRate > 0) {
        this.generateInvoiceForm.amount = (baseRate * days).toFixed(2);
        return;
      }

      const incubator = this.generateInvoiceTarget?.incubator;
      const incubatorType =
        incubator?.incubator_type || incubator?.incubatorType;
      const fallbackPricePerDay =
        incubatorType?.price_per_day ||
        incubatorType?.pricePerDay ||
        incubatorType?.price;

      if (fallbackPricePerDay) {
        const price = parseFloat(fallbackPricePerDay);
        if (!isNaN(price) && price > 0) {
          this.generateInvoiceForm.amount = (price * days).toFixed(2);
          return;
        }
      }

      // Fallback when price is missing
      this.generateInvoiceForm.amount = "";
    },

    // Confirm generate invoice for overdue incubator
    async confirmGenerateInvoice() {
      // Reset errors
      this.generateInvoiceError.amount = "";
      this.generateInvoiceError.paid_until = "";
      this.generateInvoiceError.numberOfDays = "";

      // Validations
      let hasError = false;
      if (
        !this.generateInvoiceForm.amount ||
        this.generateInvoiceForm.amount <= 0
      ) {
        this.generateInvoiceError.amount = this.$t("validation.required");
        hasError = true;
      }

      // If custom preset is selected, ensure numberOfDays is provided
      if (this.generateInvoicePresetDuration === "custom") {
        if (
          !this.generateInvoiceForm.numberOfDays ||
          this.generateInvoiceForm.numberOfDays <= 0
        ) {
          this.generateInvoiceError.numberOfDays = this.$t(
            "validation.required"
          );
          hasError = true;
        } else {
          // Recalculate to ensure paid_until is set
          this.calculatePaidUntilFromDays();
        }
      }

      // Ensure paid_until is set
      if (!this.generateInvoiceForm.paid_until) {
        this.generateInvoiceError.paid_until = this.$t("validation.required");
        hasError = true;
      }

      if (hasError) return;

      this.loadingGenerateInvoice = true;

      try {
        const payload = {
          amount: parseFloat(this.generateInvoiceForm.amount),
          paid_until: this.formatDateTimeForAPI(
            this.generateInvoiceForm.paid_until
          ),
        };

        await this.$store.dispatch("create", {
          form: payload,
          path: `/patientCardIncubators/${this.generateInvoiceTarget.id}/generate-invoice`,
        });

        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.success"),
            variant: "success",
            text: this.$t("incubator.invoice-generated-successfully"),
          },
        });

        this.$bvModal.hide("modal-generate-invoice");
        this.generateInvoiceTarget = null;
        await this.getData();
      } catch (e) {
        console.error("Generate Invoice Error:", e);
        console.error("Error Response:", e?.response?.data);
        const errorMsg =
          e?.response?.data?.message || this.$t("common.error-occurred");
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.error"),
            variant: "danger",
            text: errorMsg,
          },
        });
      } finally {
        this.loadingGenerateInvoice = false;
      }
    },

    async ensureIncubatorDetailsWithType(item) {
      if (!item?.incubator?.id) return;

      const hasIncubatorType =
        item.incubator.incubator_type || item.incubator.incubatorType;

      if (hasIncubatorType) {
        this.generateInvoiceTarget.incubator = item.incubator;
        return;
      }

      try {
        const response = await this.$http.get(
          `/incubators/${item.incubator.id}?include=incubatorType`
        );
        const incubatorData = response?.data?.data || response?.data;
        if (incubatorData) {
          this.generateInvoiceTarget.incubator = incubatorData;
          this.setGenerateInvoiceBaseDailyRate();
        }
      } catch (error) {
        console.error("Error fetching incubator details:", error);
      }
    },

    setGenerateInvoiceBaseDailyRate() {
      const incubator = this.generateInvoiceTarget?.incubator;
      const incubatorType =
        incubator?.incubator_type || incubator?.incubatorType;

      if (!incubatorType) {
        this.generateInvoiceBaseDailyRate = 0;
        return;
      }

      const {
        price_per_day,
        pricePerDay,
        price,
        price_per_time_unit,
        pricePerTimeUnit,
        time_unit,
        timeUnit,
      } = incubatorType;

      const explicitDaily =
        price_per_day ||
        pricePerDay ||
        price ||
        price_per_time_unit ||
        pricePerTimeUnit;
      let parsed = parseFloat(explicitDaily);

      if (Number.isNaN(parsed)) {
        this.generateInvoiceBaseDailyRate = 0;
        return;
      }

      const unit = (time_unit || timeUnit || "day").toLowerCase();
      if (unit === "hour" || unit === "hours") {
        parsed *= 24;
      }

      this.generateInvoiceBaseDailyRate = parsed;
    },
  },
};
</script>

<style scoped>
/* Clock icon animation for active (green, spinning) */
.clock-icon-active {
  color: #ff9f42;
  animation: spin 3s linear infinite;
}

/* Clock icon animation for expired (red, shaking) */
.clock-icon-expired {
  color: #ea5455;
  animation: shake 2.5s ease-in-out infinite;
}

/* Spinning animation */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Shaking animation */
@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-3px);
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(3px);
  }
}

/* Table horizontal scroll - always visible */
.table-responsive,
.b-table-responsive,
.table > .table-responsive {
  overflow-x: scroll !important;
  overflow-y: visible !important;
  -webkit-overflow-scrolling: touch;
}

/* Make scrollbar always visible and styled - Webkit browsers */
.table-responsive::-webkit-scrollbar,
.b-table-responsive::-webkit-scrollbar {
  height: 14px !important;
  background-color: #f5f5f5;
  border-radius: 8px;
}

.table-responsive::-webkit-scrollbar-track,
.b-table-responsive::-webkit-scrollbar-track {
  background-color: #f5f5f5;
  border-radius: 8px;
  margin: 0 10px;
}

.table-responsive::-webkit-scrollbar-thumb,
.b-table-responsive::-webkit-scrollbar-thumb {
  background-color: #888;
  border-radius: 8px;
  border: 2px solid #f5f5f5;
}

.table-responsive::-webkit-scrollbar-thumb:hover,
.b-table-responsive::-webkit-scrollbar-thumb:hover {
  background-color: #555;
}

/* For Firefox */
.table-responsive,
.b-table-responsive {
  scrollbar-width: auto !important;
  scrollbar-color: #888 #f5f5f5;
}

/* Renew invoice button styling */
.renew-invoice-btn {
  font-size: 0.8rem;
  padding: 5px 10px;
  font-weight: 600;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: linear-gradient(135deg, #ea5455 0%, #d63031 100%);
  box-shadow: 0 2px 8px rgba(234, 84, 85, 0.3);
  transition: all 0.3s ease;
  animation: glow-pulse 2s ease-in-out infinite;
}
.renew-invoice-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(234, 84, 85, 0.5);
  background: linear-gradient(135deg, #d63031 0%, #c0392b 100%);
  animation: none;
}
.renew-invoice-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(234, 84, 85, 0.3);
}

/* Glow pulse animation */
@keyframes glow-pulse {
  0%,
  100% {
    box-shadow: 0 2px 8px rgba(234, 84, 85, 0.3);
  }
  50% {
    box-shadow: 0 4px 16px rgba(234, 84, 85, 0.6);
  }
}

/* ==== Patient Incubator (PI) Card styles ==== */
.pi-card {
  border: 2px solid #e9ecef;
  border-radius: 14px;
  overflow: visible;
  background: #fff;
  box-shadow: 0 2px 12px rgba(16, 24, 40, 0.06);
  transition: transform 0.12s ease, box-shadow 0.12s ease,
    border-color 0.12s ease;
  cursor: pointer;
}
.pi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(16, 24, 40, 0.1);
}

.pi-card--active {
  border-color: #c9f7e7;
}
.pi-card--inactive {
  border-color: #e4e7ec;
}
.pi-card--expired {
  border-color: #ffe0e0;
}

/* Header */
.pi-card__header {
  padding: 0.9rem 0.9rem 0.6rem;
}
.pi-card__icon--primary {
  color: #5e66ff;
}
.pi-card__title {
  font-weight: 700;
  line-height: 1.2;
}
.pi-card__muted {
  color: #6b7280;
  font-size: 0.8rem;
}

/* Divider */
.pi-card__divider {
  height: 1px;
  background: repeating-linear-gradient(
    to right,
    #eef2f6,
    #eef2f6 6px,
    transparent 6px,
    transparent 12px
  );
}

/* Sections */
.pi-card__section {
  padding: 0.8rem 0.9rem;
}
.pi-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.8rem 0.9rem 0.95rem;
  border-top: 1px dashed #e5e7eb;
  position: relative;
}

/* Dropdown wrapper in footer needs higher z-index */
.pi-card__footer .dropdown,
.pi-card__footer .b-dropdown {
  position: static;
}

.pi-card__footer .dropdown-menu {
  z-index: 1050 !important;
}

/* Ensure card can show dropdown above siblings */
.pi-card:has(.dropdown.show) {
  z-index: 10;
  position: relative;
}

/* Payment overdue badge in footer */
.payment-overdue-badge {
  font-size: 0.75rem;
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  font-weight: 600;
}

/* Small chip */
.pi-chip {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  background: #f3f6ff;
  border: 1px solid #e6eaff;
  color: #3b49df;
  border-radius: 9999px;
  padding: 0.2rem 0.55rem;
}
.timeline-line {
  font-size: 0.8rem;
}

/* Common part styling */
.timeline-line .tl-part {
  padding: 2px 6px;
  border-radius: 4px;
}

/* Specific colors */
.timeline-line .tl-start {
  background: rgba(0, 150, 0, 0.1);
}

.timeline-line .tl-paid {
  background: rgba(255, 200, 0, 0.1);
}

.timeline-line .tl-end {
  background: rgba(200, 0, 0, 0.1);
}

/* Arrow between items */
.timeline-line .tl-arrow {
  font-size: 12px;
  opacity: 0.6;
  margin: 2px 0;
}

/* Overdue state — highlight paid area */
.timeline-line.tl-overdue .tl-paid {
  background: rgba(255, 0, 0, 0.15);
  color: #c00;
  font-weight: bold;
}

/* Patient info box styling for modals */
.patient-info-box {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
}
</style>
